import React, { useEffect, useRef, useState } from 'react';
import {
  Configure,
  Hits,
  Index,
  InstantSearch,
  useInstantSearch,
} from 'react-instantsearch-hooks-web';
import { CustomSearchBox } from './CustomSearchBox';
import { decode } from 'html-entities';
import Image from '../../../common/Image';
import { numberFormat } from '../../../../utils/product/product';
import { seoUrlParser } from '../../../../utils/helper';
import parse from 'html-react-parser';
import { useOnClickOutside } from 'usehooks-ts';
import { useRouter } from 'next/router';
import typesenseInstantsearchAdapter from '../../../../utils/typesense/adapter';
import TS_CONFIG from '../../../../utils/typesense/config';
import { ITSImage } from '../../../../utils/typesense/types/image';
import getImgUrl from '../../../../utils/getImageUrl';

const searchClient = typesenseInstantsearchAdapter.searchClient;

const ProductHit = ({ hit }) => {
  const productLink = seoUrlParser(hit.permalink);
  const thumbnail: ITSImage = hit.thumbnail;

  return (
    <a href={productLink}>
      <div className="flex gap-4 justify-start group cursor-pointer mb-2.5 hover:bg-[#F2F2F2] py-2">
        <div>
          <div className=" w-[63px] h-[55px] overflow-hidden bg-gray-200 group-hover:opacity-75 ml-1 mt-1">
            <Image
              src={getImgUrl(thumbnail.src)}
              alt={decode(hit.name)}
              className="w-full h-full object-center object-cover"
              width={63}
              height={55}
            />
          </div>
        </div>

        <div>
          <p className="group-hover:text-brand-primary text-sm text-brand-white-hover uppercase font-bold font-montserrat mb-1">
            {decode(hit.name)}
          </p>
          <p className="group-hover:text-brand-primary mb-1">
            {parse(decode(hit.shortDescription))}
          </p>
          <p className="group-hover:text-brand-primary font-bold text-sm text-[#6E6E69]">
            {parseFloat(hit.salePrice.AUD) > 0 ? (
              <>
                <span className="text-brand-second-gray underline">
                  {numberFormat(hit.regularPrice.AUD)}
                </span>
                <span className="ml-1 text-black">
                  {numberFormat(hit.salePrice.AUD)}
                </span>
              </>
            ) : (
              <span className="text-black">{numberFormat(hit.price.AUD)}</span>
            )}
          </p>
        </div>
      </div>
    </a>
  );
};

const CategoryHit = ({ hit }) => {
  return (
    <a href={seoUrlParser(hit.permalink)}>
      <div className="group flex justify-start items-center gap-6 px-3.5 py-3 cursor-pointer mb-2.5 hover:bg-[#F2F2F2]">
        <div>
          <svg
            width="25"
            height="27"
            viewBox="0 0 25 27"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M19.5657 6.12138C19.5657 6.56341 19.3901 6.98733 19.0776 7.29989C18.765 7.61246 18.3411 7.78805 17.8991 7.78805C17.457 7.78805 17.0331 7.61246 16.7206 7.29989C16.408 6.98733 16.2324 6.56341 16.2324 6.12138C16.2324 5.67936 16.408 5.25543 16.7206 4.94287C17.0331 4.63031 17.457 4.45472 17.8991 4.45472C18.3411 4.45472 18.765 4.63031 19.0776 4.94287C19.3901 5.25543 19.5657 5.67936 19.5657 6.12138ZM10.2431 1.31872C11.0487 0.550437 12.1191 0.121692 13.2324 0.121384H20.5644C21.046 0.121209 21.523 0.215921 21.968 0.400109C22.413 0.584298 22.8174 0.854355 23.158 1.19485C23.4986 1.53535 23.7688 1.93962 23.9532 2.38457C24.1375 2.82952 24.2324 3.30642 24.2324 3.78805V10.392C24.2324 10.9668 24.118 11.5358 23.896 12.066C23.674 12.5961 23.3487 13.0768 22.9391 13.48L13.6777 22.5934C13.1143 23.1477 12.3548 23.4573 11.5643 23.4548C10.7739 23.4523 10.0164 23.1379 9.45639 22.58L1.11506 14.2787C0.831127 13.9962 0.606647 13.6597 0.454858 13.289C0.303069 12.9183 0.227045 12.521 0.23127 12.1205C0.235495 11.72 0.319884 11.3243 0.479459 10.957C0.639034 10.5896 0.870564 10.2579 1.16039 9.98138L10.2431 1.31872ZM13.2337 2.12138C12.6337 2.12138 12.0564 2.35205 11.6231 2.76672L2.53906 11.428C2.44234 11.5202 2.36509 11.6309 2.31185 11.7535C2.25862 11.876 2.23049 12.008 2.22912 12.1416C2.22775 12.2752 2.25318 12.4078 2.30389 12.5314C2.35461 12.655 2.42958 12.7672 2.52439 12.8614L10.8671 21.1627C11.0538 21.3485 11.3062 21.4531 11.5696 21.4539C11.8329 21.4546 12.086 21.3514 12.2737 21.1667L21.5337 12.0547C21.7544 11.8377 21.9298 11.5789 22.0495 11.2935C22.1692 11.008 22.2309 10.7016 22.2311 10.392V3.78805C22.2311 2.86805 21.4844 2.12138 20.5644 2.12138H13.2311H13.2337ZM23.8737 14.4307L21.9071 16.3654C21.8537 16.4387 21.7937 16.5067 21.7284 16.5721L14.6164 23.544C13.8025 24.3455 12.7051 24.7932 11.5628 24.79C10.4206 24.7867 9.32576 24.3328 8.51639 23.5267L5.94173 20.9614L2.72706 17.7801C2.67615 17.7298 2.6285 17.6764 2.58439 17.62L0.411059 15.4587C0.200574 16.1055 0.174148 16.7981 0.334736 17.4591C0.495324 18.12 0.836596 18.7233 1.32039 19.2014L7.12839 24.9494C8.31042 26.1191 9.90508 26.777 11.568 26.781C13.231 26.785 14.8288 26.1347 16.0164 24.9707L23.1284 17.9987C23.6155 17.521 23.9597 16.9169 24.1222 16.2543C24.2847 15.5917 24.2592 14.8969 24.0484 14.248C23.9915 14.3098 23.9333 14.3702 23.8737 14.4294V14.4307Z"
              fill="#58585B"
              className="text-brand-primary"
            />
          </svg>
        </div>
        <p className="group-hover:text-brand-primary text-sm text-brand-white-hover uppercase font-bold font-montserrat">
          {parse(hit.name)}
        </p>
      </div>
    </a>
  );
};

function NoResultsBoundary({ children, fallback }) {
  const { results } = useInstantSearch();

  // The `__isArtificial` flag makes sure not to display the No Results message
  // when no hits have been returned yet.
  if (!results.__isArtificial && results.nbHits === 0) {
    return (
      <>
        {fallback}
        <div hidden>{children}</div>
      </>
    );
  }

  return children;
}

const NoResults = () => {
  const { indexUiState } = useInstantSearch();

  return (
    <div>
      <p>
        No results for{' '}
        <strong>
          <q>{indexUiState.query}</q>
        </strong>
        .
      </p>
    </div>
  );
};

export default function Search() {
  const { asPath } = useRouter();
  const searchResultRef = useRef(null);
  const [showResult, setShowResult] = useState(false);
  const [searchTerm, setSearchTerm] = useState<string>('');

  useOnClickOutside(searchResultRef, () => {
    setShowResult(false);
  });

  // On router change hide result
  useEffect(() => {
    setShowResult(false);
  }, [asPath]);

  return (
    <div className="relative" ref={searchResultRef}>
      <InstantSearch
        indexName={TS_CONFIG.collectionNames.product}
        searchClient={searchClient}
      >
        <CustomSearchBox
          onFocus={() => setShowResult(true)}
          setSearchTerm={setSearchTerm}
        />

        {searchTerm.length > 0 && showResult === true && (
          <div className="px-[30px] py-[20px] absolute bg-white min-w-[320px] md:min-w-[768px]  rounded-sm right-0 md:-right-1 z-20 shadow-lg border ">
            <div>
              <NoResultsBoundary fallback={<NoResults />}>
                <Index indexName={TS_CONFIG.collectionNames.taxonomy}>
                  <p className="text-[#A09D9A] uppercase text-xs font-bold mb-2.5">
                    CATEGORIES
                  </p>
                  <Configure hitsPerPage={3} />
                  <div>
                    <Hits hitComponent={CategoryHit} />
                  </div>
                  <div className="border-b border-b-[#DEDEDE] my-2.5" />
                </Index>

                <Index indexName={TS_CONFIG.collectionNames.product}>
                  <p className="text-[#A09D9A] uppercase text-xs font-bold mb-2.5">
                    PRODUCTS
                  </p>
                  <Configure hitsPerPage={5} />
                  <div>
                    <Hits hitComponent={ProductHit} />
                  </div>
                  <div className="border-b border-b-[#DEDEDE] my-2.5" />
                </Index>
              </NoResultsBoundary>
            </div>
          </div>
        )}
      </InstantSearch>
    </div>
  );
}
